import AddOutlined from '@mui/icons-material/AddOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import RemoveOutlined from '@mui/icons-material/RemoveOutlined';
import { Box, Divider, IconButton, TextField, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParsedInvoiceProduct } from 'shared/types/Invoice';

import { InventoryIcon } from '@/assets/svg';
import { Button } from '@/components';
import { COLOR } from '@/theme/colors';

import SelectionOutlinedBox from '../SelectionOutlinedBox';

import { SelectInvoicingProductsModal } from './components';
import CreateInvoicingProductModal from './components/CreateInvoicingProductModal';
import { Props } from './types';

function SelectInvoicingProductsSection({
    onRemoveProduct,
    onSelectProducts,
    onProductQuantityChanged,
    selectedProducts,
    testIdPrefix,
    disabled,
    currencySymbol,
    buttonsPosition = 'bottom',
    sectionLabel,
    allowCreate = false,
    userId,
    organisationId,
    subtotal,
    showTax,
    productsTaxRate,
    defaultProductsTaxRate,
    onProductsTaxRateChange,
    calculatedProductsTax,
}: Props) {
    const { t } = useTranslation();

    const [isSelectModalOpen, setIsSelectModalOpen] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isTaxEditMode, setIsTaxEditMode] = useState(false);
    const [taxEditValue, setTaxEditValue] = useState('');
    const [taxError, setTaxError] = useState('');

    const openSelectModal = useCallback(() => {
        setIsSelectModalOpen(true);
    }, []);

    const closeSelectModal = useCallback(() => {
        setIsSelectModalOpen(false);
    }, []);

    const openCreateModal = useCallback(() => {
        setIsCreateModalOpen(true);
    }, []);

    const closeCreateModal = useCallback(() => {
        setIsCreateModalOpen(false);
    }, []);

    const onProductCreated = useCallback(
        (product: ParsedInvoiceProduct) => {
            onSelectProducts([...selectedProducts, product]);
        },
        [onSelectProducts, selectedProducts],
    );

    const handleTaxEdit = useCallback(() => {
        setTaxEditValue(productsTaxRate || '0');
        setTaxError('');
        setIsTaxEditMode(true);
    }, [productsTaxRate]);

    const handleTaxConfirm = useCallback(() => {
        const formatted = taxEditValue.replace(',', '.');
        if (!formatted || isNaN(Number(formatted))) {
            setTaxError(t('Invoice:tax_invalid'));
            return;
        }
        if (Number(formatted) < 0) {
            setTaxError(t('Invoice:tax_negative'));
            return;
        }
        setTaxError('');
        onProductsTaxRateChange?.(formatted);
        setIsTaxEditMode(false);
    }, [taxEditValue, onProductsTaxRateChange, t]);

    const handleTaxCancel = useCallback(() => {
        setIsTaxEditMode(false);
    }, []);

    const isTaxCustomized = productsTaxRate !== defaultProductsTaxRate;

    return (
        <SelectionOutlinedBox
            onSelect={openSelectModal}
            onAdd={openCreateModal}
            disabled={disabled}
            title={t('Invoice:labels:products')}
            selectLabel={t('Invoice:buttons:select_products')}
            addLabel={t('Invoice:buttons:create_products')}
            SelectIcon={InventoryIcon}
            buttonsPosition={buttonsPosition}
            sectionLabel={sectionLabel}
            selectButtonTestId={`${testIdPrefix}-SelectProductsButton`}
            addButtonTestId={`${testIdPrefix}-CreateProductButton`}
            allowCreate={allowCreate}
        >
            {!selectedProducts.length ? (
                <Typography
                    color={COLOR.regentGray}
                    variant="body2"
                    fontSize="small"
                    data-test-id={`${testIdPrefix}-SelectedProductsEmptyStateText`}
                    sx={{
                        display: 'flex',
                        alignSelf: 'center',
                        justifyContent: 'center',
                        flex: 1,
                    }}
                >
                    {t('Invoice:labels:no_products_added')}
                </Typography>
            ) : (
                <Box className="flex flex-col gap-2 w-full">
                    {selectedProducts.map((product, index) => (
                        <Box
                            key={product.invoicingProductId || index}
                            className="flex items-center gap-1 rounded-lg border border-gray-200 p-3 bg-athensGrayDarker"
                            data-test-id={`${testIdPrefix}-Product-${index}`}
                        >
                            <IconButton
                                size="small"
                                onClick={() =>
                                    onProductQuantityChanged(
                                        index,
                                        Math.max(1, product.quantity - 1),
                                    )
                                }
                                disabled={product.quantity <= 1}
                                data-test-id={`${testIdPrefix}-DecrementProduct-${index}`}
                            >
                                <RemoveOutlined
                                    sx={{
                                        fontSize: 14,
                                        color: COLOR.ebonyClay,
                                    }}
                                />
                            </IconButton>
                            <TextField
                                type="number"
                                value={product.quantity}
                                onChange={(e) => {
                                    const val = Number(e.target.value);
                                    if (val >= 1) {
                                        onProductQuantityChanged(index, val);
                                    }
                                }}
                                inputProps={{
                                    min: 1,
                                    maxLength: 3,
                                    style: {
                                        textAlign: 'center',
                                        padding: '4px 0',
                                        MozAppearance: 'textfield',
                                    },
                                }}
                                size="small"
                                sx={{
                                    width: 40,
                                    '& .MuiOutlinedInput-root': {
                                        height: 32,
                                    },
                                    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                        {
                                            display: 'none',
                                        },
                                }}
                            />
                            <IconButton
                                size="small"
                                onClick={() =>
                                    onProductQuantityChanged(
                                        index,
                                        product.quantity + 1,
                                    )
                                }
                                data-test-id={`${testIdPrefix}-IncrementProduct-${index}`}
                            >
                                <AddOutlined
                                    sx={{
                                        fontSize: 14,
                                        color: COLOR.ebonyClay,
                                    }}
                                />
                            </IconButton>
                            <Typography
                                className="flex-1 truncate"
                                fontWeight="bold"
                            >
                                {product.name}
                            </Typography>
                            <DeleteOutlineRoundedIcon
                                sx={{
                                    cursor: 'pointer',
                                    color: COLOR.regentGray,
                                    '&:hover': {
                                        color: COLOR.ebonyClay,
                                    },
                                }}
                                onClick={() => onRemoveProduct(index)}
                                data-test-id={`${testIdPrefix}-RemoveProduct-${index}`}
                            />
                            <Typography
                                variant="body2"
                                fontWeight="bold"
                                sx={{
                                    minWidth: 80,
                                    textAlign: 'right',
                                }}
                            >
                                {currencySymbol}
                                {(
                                    Number(product.price) * product.quantity
                                ).toFixed(2)}
                            </Typography>
                        </Box>
                    ))}
                    {subtotal !== undefined && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginLeft: '120px',
                                    marginRight: '12px',
                                }}
                            >
                                <Typography fontWeight="bold" fontSize={14}>
                                    {t(
                                        'InvoiceSystem:invoice_summary:subtotal_products',
                                    )}
                                </Typography>
                                <Typography fontWeight="bold" fontSize={14}>
                                    {currencySymbol}
                                    {subtotal}
                                </Typography>
                            </Box>
                        </>
                    )}
                    {showTax && selectedProducts.length > 0 && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    marginLeft: '120px',
                                    marginRight: '12px',
                                }}
                            >
                                <Box className="flex flex-col gap-1">
                                    <Box className="flex items-center gap-1">
                                        <Typography
                                            fontWeight="bold"
                                            fontSize={14}
                                        >
                                            {t('InvoiceSystem:sales_tax')}
                                        </Typography>
                                        <Typography fontSize={14}>
                                            {productsTaxRate || '0'}%
                                        </Typography>
                                        <Typography
                                            fontSize={14}
                                            color={
                                                isTaxCustomized
                                                    ? COLOR.darkYellow
                                                    : COLOR.regentGray
                                            }
                                        >
                                            (
                                            {isTaxCustomized
                                                ? 'customized'
                                                : 'default'}
                                            )
                                        </Typography>
                                        {!isTaxEditMode && (
                                            <Typography
                                                fontSize={14}
                                                sx={{
                                                    cursor: 'pointer',
                                                    ml: 0.5,
                                                }}
                                                color={COLOR.deepCerulean}
                                                onClick={handleTaxEdit}
                                            >
                                                {t('Actions:edit')}
                                            </Typography>
                                        )}
                                    </Box>
                                    {isTaxEditMode && (
                                        <Box className="flex items-center gap-2">
                                            <TextField
                                                type="text"
                                                value={taxEditValue}
                                                onChange={(e) => {
                                                    setTaxEditValue(
                                                        e.target.value,
                                                    );
                                                    setTaxError('');
                                                }}
                                                error={!!taxError}
                                                inputProps={{ maxLength: 5 }}
                                                size="small"
                                                sx={{ width: 70 }}
                                                data-test-id={`${testIdPrefix}-ProductsTaxRateInput`}
                                            />
                                            <Button
                                                caption={t('Actions:confirm')}
                                                onClick={handleTaxConfirm}
                                                variant="contained"
                                                size="small"
                                                sx={{
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    backgroundColor:
                                                        COLOR.deepCerulean,
                                                    color: COLOR.white,
                                                    '&:hover': {
                                                        backgroundColor:
                                                            COLOR.deepCerulean,
                                                        opacity: 0.9,
                                                    },
                                                }}
                                                data-test-id={`${testIdPrefix}-ProductsTaxRateConfirm`}
                                            />
                                            <Typography
                                                sx={{ cursor: 'pointer' }}
                                                color={COLOR.deepCerulean}
                                                onClick={handleTaxCancel}
                                            >
                                                {t('Actions:cancel')}
                                            </Typography>
                                        </Box>
                                    )}
                                    {taxError && (
                                        <Typography color="error" fontSize={12}>
                                            {taxError}
                                        </Typography>
                                    )}
                                </Box>
                                <Typography fontWeight="bold" fontSize={14}>
                                    {currencySymbol}
                                    {(calculatedProductsTax ?? 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            )}
            <SelectInvoicingProductsModal
                close={closeSelectModal}
                isOpen={isSelectModalOpen}
                onSelectProducts={onSelectProducts}
                selectedProducts={selectedProducts}
            />
            {allowCreate && userId && organisationId && (
                <CreateInvoicingProductModal
                    close={closeCreateModal}
                    isOpen={isCreateModalOpen}
                    onProductCreated={onProductCreated}
                    userId={userId}
                    organisationId={organisationId}
                />
            )}
        </SelectionOutlinedBox>
    );
}

export default SelectInvoicingProductsSection;
